import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, Users, Target, Activity, Bot } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";
import AnimatedCounter from "@/components/AnimatedCounter";

const dashCards = [
  { label: "Revenue Generated", value: 8.4, prefix: "₹", suffix: "Cr", decimals: 1, color: "from-neon-orange to-neon-magenta", icon: TrendingUp, pos: "left-[3%] top-[18%]", delay: "" },
  { label: "Leads Captured", value: 24817, color: "from-neon-cyan to-neon-blue", icon: Users, pos: "right-[2%] top-[14%]", delay: "floaty-delay-1" },
  { label: "Conversion Rate", value: 6.2, suffix: "%", decimals: 1, color: "from-neon-magenta to-neon-purple", icon: Target, pos: "left-[6%] bottom-[18%]", delay: "floaty-delay-2" },
  { label: "Growth Score", value: 94, color: "from-neon-cyan to-neon-purple", icon: Activity, pos: "right-[5%] bottom-[22%]", delay: "floaty-delay-3" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const navigate = useNavigate();

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-32 pb-24 overflow-hidden"
    >
      {/* Cinematic background image with neon overlay */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="/images/hero.png"
          alt=""
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bg-0/40 via-bg-0/60 to-bg-0" />
        <div className="absolute inset-0 bg-[radial-gradient(80rem_50rem_at_50%_-10%,rgba(0,240,255,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(60rem_40rem_at_15%_110%,rgba(255,0,127,0.15),transparent_60%)]" />
      </motion.div>

      {/* Animated grid overlay */}
      <svg
        className="absolute inset-0 -z-10 deco opacity-[0.12]"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse">
            <path d="M 56 0 L 0 0 0 56" fill="none" stroke="white" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      <motion.div style={{ opacity: fade }} className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-2 mb-7"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-[11px] font-mono tracking-[0.18em] uppercase">
            <Sparkles size={12} className="text-neon-cyan" />
            Growth Infrastructure Co.
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.18 }}
          className="max-w-5xl text-[2.6rem] sm:text-6xl lg:text-[5rem] leading-[0.95] font-extrabold tracking-tight uppercase"
        >
          We build systems that turn{" "}
          <span className="text-gradient-warm">attention</span>{" "}
          into{" "}
          <span className="text-gradient-neon">revenue.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-white/65 leading-relaxed"
        >
          Novarise Works helps businesses build their brand, generate demand,
          and scale through AI-powered growth systems — engineered for compounding outcomes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <MagneticButton
            data-testid="hero-book-call"
            tone="warm"
            onClick={() => navigate("/contact")}
          >
            Book Strategy Call <ArrowRight size={16} />
          </MagneticButton>
          <MagneticButton
            data-testid="hero-explore-services"
            tone="ghost"
            onClick={() => navigate("/services")}
          >
            Explore Services
          </MagneticButton>
        </motion.div>

        {/* AI Automation pill */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.85 }}
          className="mt-10 inline-flex items-center gap-3 glass rounded-full pl-2 pr-4 py-2"
        >
          <span className="relative w-8 h-8 rounded-full bg-gradient-to-tr from-neon-cyan to-neon-purple flex items-center justify-center">
            <Bot size={16} />
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
          </span>
          <span className="font-mono text-xs tracking-[0.18em] uppercase text-white/80">
            AI Automation · Active
          </span>
        </motion.div>
      </motion.div>

      {/* Floating dashboard cards */}
      <div className="pointer-events-none absolute inset-0 hidden lg:block">
        {dashCards.map((c, i) => (
          <FloatCard key={c.label} idx={i} {...c} />
        ))}
      </div>
    </section>
  );
}

function FloatCard({ label, value, prefix, suffix, decimals, color, icon: Icon, pos, delay, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, delay: 0.7 + idx * 0.12, ease: "easeOut" }}
      className={`absolute ${pos} ${delay} floaty pointer-events-auto`}
    >
      <div className="relative glass rounded-2xl px-5 py-4 min-w-[210px] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.9)]">
        <div className={`absolute -inset-px rounded-2xl bg-gradient-to-br ${color} opacity-20 -z-10 blur-md`} />
        <div className="flex items-center justify-between mb-2">
          <span className="overline">{label}</span>
          <span className={`w-7 h-7 rounded-md bg-gradient-to-br ${color} flex items-center justify-center`}>
            <Icon size={14} />
          </span>
        </div>
        <div className="font-mono text-2xl font-extrabold tracking-tight">
          <AnimatedCounter value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
        </div>
        <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${50 + idx * 12}%` }}
            transition={{ duration: 1.6, delay: 1 + idx * 0.2 }}
            className={`h-full bg-gradient-to-r ${color}`}
          />
        </div>
      </div>
    </motion.div>
  );
}
